import React, { useEffect, useState } from 'react';
import { Group, Text, TextInput } from '@mantine/core';
import { useTheme } from '../../../context/ThemeProvider.tsx';
import { ProgressiveTier, RateDetail } from '../../../models/ParkingRate.ts';
import { type RateGroup } from '../../../hooks/parking-services/useParkingRateGroups.ts';
import { DAY_SHORT, fmt } from '../../../utils/ParkingRateTime.ts';
import { parseRate } from '../../../utils/ParkingRateCardUtils.ts';

interface ParkingRateGroupCardProps {
  group: RateGroup<RateDetail>;
  columnLabels?: string[];
  isEditing: boolean;
  // 帶 groupId 當參數，讓呼叫端可以直接傳穩定的函式參照（例如 useCallback 包過的
  // useParkingRateGroups 回傳值），不需要在 .map() 裡另外包一層新的箭頭函式──
  // 否則 React.memo 的淺比較每次都會判定 props 有變，記憶化就永遠不會生效。
  onEdit: (groupId: number) => void;
  onConfirm: (groupId: number, rate: RateDetail) => void;
  onCancel: (groupId: number) => void;
  onDelete: (groupId: number) => void;
}

const ParkingRateGroupCard = React.memo<ParkingRateGroupCardProps>(
  ({
    group,
    columnLabels,
    isEditing,
    onEdit,
    onConfirm,
    onCancel,
    onDelete,
  }) => {
    const [draftRateMode, setDraftRateMode] = useState<
      'progressive' | 'per_entry'
    >(group.rate?.type === 'PER_ENTRY' ? 'per_entry' : 'progressive');
    const [draftFee, setDraftFee] = useState(
      group.rate?.type === 'PER_ENTRY'
        ? String((group.rate as Extract<RateDetail, { type: 'PER_ENTRY' }>).fee)
        : '',
    );
    const [draftTiers, setDraftTiers] = useState<
      Array<{
        startMinute: string;
        endMinute: string;
        intervalMinutes: string;
        fee: string;
      }>
    >(() => {
      if (group.rate?.type !== 'PROGRESSIVE') {
        return [
          { startMinute: '0', endMinute: '', intervalMinutes: '', fee: '' },
        ];
      }
      return (
        group.rate as Extract<RateDetail, { type: 'PROGRESSIVE' }>
      ).tiers.map((t) => ({
        startMinute: String(t.startMinute),
        endMinute: t.endMinute !== null ? String(t.endMinute) : '',
        intervalMinutes: String(t.intervalMinutes),
        fee: String(t.fee),
      }));
    });
    const [hoverDel, setHoverDel] = useState(false);
    const { colorScheme } = useTheme();

    useEffect(() => {
      if (!isEditing) return;
      if (group.rate === null) {
        setDraftRateMode('progressive');
        setDraftFee('');
        setDraftTiers([
          { startMinute: '0', endMinute: '', intervalMinutes: '', fee: '' },
        ]);
        return;
      }
      const isPerEntry = group.rate.type === 'PER_ENTRY';
      setDraftRateMode(isPerEntry ? 'per_entry' : 'progressive');
      if (isPerEntry) {
        setDraftFee(
          String(
            (group.rate as Extract<RateDetail, { type: 'PER_ENTRY' }>).fee,
          ),
        );
        setDraftTiers([
          { startMinute: '0', endMinute: '', intervalMinutes: '', fee: '' },
        ]);
      } else {
        setDraftFee('');
        const prog = group.rate as Extract<RateDetail, { type: 'PROGRESSIVE' }>;
        setDraftTiers(
          prog.tiers.map((t) => ({
            startMinute: String(t.startMinute),
            endMinute: t.endMinute !== null ? String(t.endMinute) : '',
            intervalMinutes: String(t.intervalMinutes),
            fee: String(t.fee),
          })),
        );
      }
    }, [isEditing, group.rate]);

    const addTier = () => {
      if (draftTiers.length === 1) {
        setDraftTiers([
          ...draftTiers,
          { startMinute: '', endMinute: '', intervalMinutes: '', fee: '' },
        ]);
      } else {
        const next = [...draftTiers];
        next.splice(draftTiers.length - 1, 0, {
          startMinute: '',
          endMinute: '',
          intervalMinutes: '',
          fee: '',
        });
        setDraftTiers(next);
      }
    };

    const handleConfirm = () => {
      if (draftRateMode === 'per_entry') {
        onConfirm(group.id, {
          type: 'PER_ENTRY',
          fee: parseInt(draftFee) || 0,
        });
      } else {
        const tiers: ProgressiveTier[] = draftTiers.map((t, i) => ({
          startMinute: i === 0 ? 0 : parseInt(t.startMinute) || 0,
          endMinute:
            i === draftTiers.length - 1 ? null : parseInt(t.endMinute) || null,
          intervalMinutes: parseInt(t.intervalMinutes) || 0,
          fee: parseInt(t.fee) || 0,
        }));
        onConfirm(group.id, { type: 'PROGRESSIVE', tiers });
      }
    };

    const renderViewMode = () => {
      const rate = group.rate;
      if (rate === null) {
        return (
          <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
            尚未設定費率
          </span>
        );
      }
      const parsed = parseRate(rate);
      if (parsed.kind === 'free') {
        return <span style={{ fontSize: 15, fontWeight: 700 }}>免費</span>;
      }
      if (parsed.kind === 'per_entry') {
        return (
          <>
            <span style={{ fontSize: 18, fontWeight: 700 }}>{parsed.fee}</span>
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 4 }}>
              元 / 次
            </span>
          </>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {parsed.tiers.map((t, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 0,
              }}
            >
              {t.timeLabel && (
                <span
                  style={{
                    fontSize: 11,
                    color: '#9ca3af',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.timeLabel}：
                </span>
              )}
              {t.isFree ? (
                <span style={{ fontSize: 15, fontWeight: 700 }}>免費</span>
              ) : (
                <Group style={{ gap: 4 }}>
                  <span
                    style={{
                      fontSize: 11,
                      color: '#9ca3af',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    每 {t.intervalMinutes} 分鐘
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{t.fee}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>元</span>
                </Group>
              )}
            </div>
          ))}
        </div>
      );
    };

    const blockHeader = (
      <div
        style={{
          padding: '10px 12px',
          background: isEditing ? group.color + '12' : '#f9fafb',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div
          style={{
            width: 4,
            minHeight: 36,
            borderRadius: 2,
            background: group.color,
            flexShrink: 0,
            marginTop: 2,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {group.blocks.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: '#9ca3af',
                paddingTop: 8,
                paddingBottom: 4,
                fontStyle: 'italic',
              }}
            >
              在右側格子拖曳來新增時段
            </div>
          ) : (
            group.blocks.map((b) => (
              <div
                key={b.id}
                style={{
                  display: 'flex',
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{ display: 'flex', gap: 3, flexWrap: 'wrap', flex: 1 }}
                >
                  {b.days.map((d) => (
                    <span
                      key={d}
                      style={{
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 600,
                        background: group.color + '22',
                        color: group.color,
                      }}
                    >
                      {columnLabels
                        ? (() => {
                            const parts = columnLabels[d].split('\n');
                            if (parts.length >= 2) {
                              const [date, dow] = parts;
                              return `${date} (${dow?.slice(-1) ?? ''})`;
                            }
                            return parts[0].replace('週', '');
                          })()
                        : DAY_SHORT[d]}
                    </span>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#374151',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {fmt(b.start)} –{' '}
                  {fmt(b.end + 1) === '24:00' ? '23:59' : fmt(b.end + 1)}
                </div>
              </div>
            ))
          )}
        </div>
        <button
          onClick={() => onDelete(group.id)}
          onMouseEnter={() => setHoverDel(true)}
          onMouseLeave={() => setHoverDel(false)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            flexShrink: 0,
            lineHeight: 1,
            border: hoverDel ? '1px solid #fca5a5' : '1px solid #e5e7eb',
            background: hoverDel ? '#fef2f2' : '#fff',
            color: hoverDel ? '#ef4444' : '#bbb',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          ×
        </button>
      </div>
    );

    return (
      <div
        style={{
          border: isEditing
            ? `1.5px solid ${group.color}99`
            : '1px solid #e5e7eb',
          borderRadius: 10,
          overflow: 'hidden',
          marginBottom: 10,
          background: isEditing
            ? colorScheme === 'light'
              ? group.color + '08'
              : group.color + '10'
            : colorScheme === 'light'
              ? 'white'
              : 'black',
        }}
      >
        {blockHeader}

        {isEditing ? (
          <div style={{ padding: '10px 12px' }}>
            {/* 收費模式切換 */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {(['progressive', 'per_entry'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => {
                    setDraftRateMode(mode);
                    setDraftFee('');
                    setDraftTiers([
                      {
                        startMinute: '0',
                        endMinute: '',
                        intervalMinutes: '',
                        fee: '',
                      },
                    ]);
                  }}
                  style={{
                    flex: 1,
                    padding: '5px 0',
                    borderRadius: 7,
                    border:
                      draftRateMode === mode
                        ? `1.5px solid ${group.color}`
                        : '1.5px solid #e5e7eb',
                    background:
                      draftRateMode === mode ? group.color + '18' : '#f9fafb',
                    color: draftRateMode === mode ? group.color : '#9ca3af',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {mode === 'progressive' ? '累進' : '計次'}
                </button>
              ))}
            </div>

            {/* 輸入欄位 */}
            {draftRateMode === 'per_entry' ? (
              <Group style={{ alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <TextInput
                  value={draftFee}
                  onChange={(e) => setDraftFee(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Text
                  style={{
                    fontSize: 12,
                    color: '#9ca3af',
                    whiteSpace: 'nowrap',
                  }}
                >
                  元 / 次
                </Text>
              </Group>
            ) : (
              <div style={{ marginBottom: 10 }}>
                {draftTiers.map((tier, i) => {
                  const isOnly = draftTiers.length === 1;
                  const isFirst = i === 0 && !isOnly;
                  const isLast = i === draftTiers.length - 1 && !isOnly;
                  const isMiddle = !isOnly && !isFirst && !isLast;
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        paddingTop: 8,
                        paddingBottom: 8,
                        borderBottom:
                          !isOnly && !isLast ? '1px dashed #e3e3e3' : null,
                      }}
                    >
                      {/* 第一行：時間範圍（isOnly 時省略） */}
                      {!isOnly && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 4,
                          }}
                        >
                          <Group gap={4}>
                            {isFirst && (
                              <>
                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: '#9ca3af',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  前
                                </Text>
                                <TextInput
                                  size={'xs'}
                                  value={tier.endMinute}
                                  onChange={(e) => {
                                    const n = [...draftTiers];
                                    n[i] = {
                                      ...n[i],
                                      endMinute: e.target.value,
                                    };
                                    setDraftTiers(n);
                                  }}
                                  style={{ width: 44 }}
                                />
                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: '#9ca3af',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  分鐘
                                </Text>
                              </>
                            )}
                            {isMiddle && (
                              <>
                                <Text
                                  style={{ fontSize: 11, color: '#9ca3af' }}
                                >
                                  第
                                </Text>
                                <TextInput
                                  size={'xs'}
                                  value={tier.startMinute}
                                  onChange={(e) => {
                                    const n = [...draftTiers];
                                    n[i] = {
                                      ...n[i],
                                      startMinute: e.target.value,
                                    };
                                    setDraftTiers(n);
                                  }}
                                  style={{ width: 44 }}
                                />
                                <Text
                                  style={{ fontSize: 11, color: '#9ca3af' }}
                                >
                                  ~
                                </Text>
                                <TextInput
                                  size={'xs'}
                                  value={tier.endMinute}
                                  onChange={(e) => {
                                    const n = [...draftTiers];
                                    n[i] = {
                                      ...n[i],
                                      endMinute: e.target.value,
                                    };
                                    setDraftTiers(n);
                                  }}
                                  style={{ width: 44 }}
                                />
                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: '#9ca3af',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  分鐘
                                </Text>
                              </>
                            )}
                            {isLast && (
                              <>
                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: '#9ca3af',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  第
                                </Text>
                                <TextInput
                                  size={'xs'}
                                  value={tier.startMinute}
                                  onChange={(e) => {
                                    const n = [...draftTiers];
                                    n[i] = {
                                      ...n[i],
                                      startMinute: e.target.value,
                                    };
                                    setDraftTiers(n);
                                  }}
                                  style={{ width: 44 }}
                                />
                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: '#9ca3af',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  分鐘後
                                </Text>
                              </>
                            )}
                          </Group>
                          {!isOnly && (
                            <button
                              onClick={() =>
                                setDraftTiers(
                                  draftTiers.filter((_, idx) => idx !== i),
                                )
                              }
                              style={{
                                width: 14,
                                height: 14,
                                border: '1px solid #fca5a5',
                                borderRadius: 3,
                                background: '#fef2f2',
                                color: '#ef4444',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                flexShrink: 0,
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      )}

                      {/* 第二行：費率 + 刪除按鈕 */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: !isOnly ? 'flex-end' : 'flex-start',
                          gap: 4,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 11,
                            color: '#9ca3af',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          每
                        </Text>
                        <TextInput
                          size={'xs'}
                          value={tier.intervalMinutes}
                          onChange={(e) => {
                            const n = [...draftTiers];
                            n[i] = { ...n[i], intervalMinutes: e.target.value };
                            setDraftTiers(n);
                          }}
                          style={{ width: isOnly ? 100 : 44 }}
                        />
                        <Text
                          style={{
                            fontSize: 11,
                            color: '#9ca3af',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          分鐘
                        </Text>
                        <TextInput
                          size={'xs'}
                          value={tier.fee}
                          onChange={(e) => {
                            const n = [...draftTiers];
                            n[i] = { ...n[i], fee: e.target.value };
                            setDraftTiers(n);
                          }}
                          style={{ width: isOnly ? 100 : 44 }}
                        />
                        <Text style={{ fontSize: 11, color: '#9ca3af' }}>
                          元
                        </Text>
                      </div>
                    </div>
                  );
                })}
                <div
                  style={{
                    width: '100%',
                  }}
                >
                  <button
                    onClick={addTier}
                    style={{
                      padding: '3px 10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: 'rgba(213,213,213,0.24)',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: 12,
                      marginTop: 8,
                      width: '100%',
                    }}
                  >
                    + 新增區段
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => onCancel(group.id)}
                style={{
                  flex: 1,
                  padding: '7px 0',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                取消
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  flex: 1,
                  padding: '7px 0',
                  border: 'none',
                  borderRadius: 8,
                  background: group.color,
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                儲存
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ flex: 1 }}>{renderViewMode()}</div>
            <button
              onClick={() => onEdit(group.id)}
              style={{
                padding: '4px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                background: '#fff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              編輯
            </button>
          </div>
        )}
      </div>
    );
  },
);
ParkingRateGroupCard.displayName = 'ParkingRateGroupCard';

export default ParkingRateGroupCard;
